import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Text,
  Image,
  Select,
  HStack,
  Spinner,
  Center,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { DownloadIcon } from "@chakra-ui/icons";
import SendEmail from "../components/SendEmail";

// 현재 년월을 YYYYMM 형식으로 반환 (7일 이전에는 이전달)
const getCurrentYearMonth = () => {
  const date = new Date();
  const currentDay = date.getDate();

  // 7일 이전인 경우 이전달로 설정
  if (currentDay < 7) {
    const prevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    return `${prevMonth.getFullYear()}${String(
      prevMonth.getMonth() + 1
    ).padStart(2, "0")}`;
  }

  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const PAGE_SIZE = 15;

const Report = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(getCurrentYearMonth());
  const [settlements, setSettlements] = useState({
    melon: 0,
    genie: 0,
    vibe: 0,
    flo: 0,
    global: 0,
    youtube: 0,
    total_sum: 0,
    remaining_amount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [agreement, setAgreement] = useState(false);
  const [isBusinessAccount, setIsBusinessAccount] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const [businessRegUrl, setBusinessRegUrl] = useState("");
  const [businessBankUrl, setBusinessBankUrl] = useState("");
  const [businessRegFile, setBusinessRegFile] = useState(null);
  const [businessBankFile, setBusinessBankFile] = useState(null);
  const [hasPayoutRow, setHasPayoutRow] = useState(false);
  const [idNumber1, setIdNumber1] = useState("");
  const [idNumber2, setIdNumber2] = useState("");
  const [editBusinessFile, setEditBusinessFile] = useState(false);
  const [editIdNumber, setEditIdNumber] = useState(false);
  const [bankAccountHolder, setBankAccountHolder] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [platformDetail, setPlatformDetail] = useState([]);
  const [platformDetailLoading, setPlatformDetailLoading] = useState(false);
  const [platformDetailPage, setPlatformDetailPage] = useState(1);
  const [platformDetailTotal, setPlatformDetailTotal] = useState(0);
  const [settlementSwitch, setSettlementSwitch] = useState(true);
  const [settlementSwitchLoading, setSettlementSwitchLoading] = useState(true);
  const [tempPage, setTempPage] = useState("");
  const [showIdInput, setShowIdInput] = useState(true);
  const [isTestMode, setIsTestMode] = useState(() => {
    const saved = localStorage.getItem("isTestMode");
    return saved === "true";
  });

  // 년월 옵션 생성 (현재 월부터 과거 24개월)
  const getDateOptions = useCallback(() => {
    const options = [];
    const currentDate = new Date();
    const currentDay = currentDate.getDate();

    // 현재 월이 7일 이후인 경우에만 현재 월 포함
    const startMonth = currentDay >= 7 ? 0 : 1;

    for (let i = startMonth; i < 24; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );
      const yearMonth = `${date.getFullYear()}${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;
      options.push(yearMonth);
    }
    return options;
  }, []);

  // 소수점 8자리에서 반올림하는 함수
  const roundToEightDecimals = useCallback((num) => {
    return Math.round(num * 100000000) / 100000000;
  }, []);

  // 파일명에서 확장자 추출 함수
  const getFileExtension = useCallback((file) => {
    const name = file?.name || "";
    return name.includes(".") ? name.substring(name.lastIndexOf(".")) : "";
  }, []);

  // 플랫폼 로고 매핑
  const platformLogos = useMemo(
    () => ({
      melon: "/images/melon-logo.png",
      genie: "/images/genie-logo.png",
      vibe: "/images/vibe-logo.png",
      flo: "/images/flo-logo.png",
      global: "/images/global-logo.png",
      youtube: "/images/youtube-logo.png",
    }),
    []
  );

  // 정산 내역 조회
  const fetchPayoutHistory = useCallback(async () => {
    if (!user?.email) return;

    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from("payout_buyer")
        .select(
          "settlement_month, amount_melon, amount_genie, amount_vibe, amount_flo, amount_global, amount_youtube, amount_total, status, created_date"
        )
        .eq("user", user.email)
        .order("created_date", { ascending: false });

      if (error) throw error;

      setPayoutHistory(data || []);

      // 같은 정산월의 각 플랫폼별 amount 합계 계산
      const currentMonthData =
        data?.filter((history) => history.settlement_month === selectedDate) ||
        [];
      const currentMonthTotal = {
        total: currentMonthData.reduce(
          (sum, history) => sum + (Number(history.amount_total) || 0),
          0
        ),
      };

      // 각 플랫폼별 정산금에서 차감
      setSettlements((prev) => ({
        ...prev,
        total_sum: prev.total_sum,
        remaining_amount: roundToEightDecimals(
          Math.max(0, prev.total_sum - currentMonthTotal.total)
        ),
      }));
    } catch (error) {
      console.error("Error fetching payout history:", error);
      toast({
        title: "정산 내역 조회 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoadingHistory(false);
    }
  }, [user?.email, selectedDate, roundToEightDecimals, toast]);

  // 정산 데이터 조회 함수
  const fetchSettlements = useCallback(async () => {
    if (!user?.email) return;

    setLoading(true);
    let retryCount = 0;
    const maxRetries = 1;

    const tryFetchSettlements = async () => {
      try {
        // 테스트 모드에 따라 RPC 함수명 결정
        const normalRpcName = isTestMode
          ? "calculate_total_settlements_test"
          : "calculate_total_settlements";
        const globalRpcName = isTestMode
          ? "global_only_settlement_test"
          : "global_only_settlement";

        // 일반 정산 금액 조회 (global 제외)
        const { data: normalData, error: normalError } = await supabase.rpc(
          normalRpcName,
          {
            target_email: user.email,
            target_month: selectedDate,
          }
        );

        if (normalError) throw normalError;

        // global 정산 금액 조회
        const { data: globalData, error: globalError } = await supabase.rpc(
          globalRpcName,
          {
            target_email: user.email,
            target_month: selectedDate,
          }
        );

        if (globalError) throw globalError;

        // 두 데이터 합치기
        setSettlements({
          melon: roundToEightDecimals(normalData[0]?.melon || 0),
          genie: roundToEightDecimals(normalData[0]?.genie || 0),
          vibe: roundToEightDecimals(normalData[0]?.vibe || 0),
          flo: roundToEightDecimals(normalData[0]?.flo || 0),
          youtube: roundToEightDecimals(normalData[0]?.youtube || 0),
          global: roundToEightDecimals(globalData || 0),
          total_sum: roundToEightDecimals(
            (normalData[0]?.total_sum || 0) + (globalData || 0)
          ),
          remaining_amount: roundToEightDecimals(
            (normalData[0]?.total_sum || 0) + (globalData || 0)
          ),
        });

        await fetchPayoutHistory();
      } catch (e) {
        if (e.message?.includes("timeout") && retryCount < maxRetries) {
          retryCount++;
          console.log(`타임아웃 발생. 재시도 ${retryCount}/${maxRetries}`);
          await tryFetchSettlements();
        } else {
          toast({
            title: "정산 데이터 조회 실패",
            description: "다시 시도해주세요.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
          setSettlements({
            melon: 0,
            genie: 0,
            vibe: 0,
            flo: 0,
            global: 0,
            youtube: 0,
            total_sum: 0,
            remaining_amount: 0,
          });
        }
      }
    };

    await tryFetchSettlements();
    setLoading(false);
  }, [
    user?.email,
    selectedDate,
    roundToEightDecimals,
    fetchPayoutHistory,
    toast,
    isTestMode,
  ]);

  // 정산 신청 처리
  const handlePayoutRequest = useCallback(async () => {
    if (!agreement || !user?.email) return;

    setSubmitting(true);
    try {
      // 파일 업로드 처리
      let regUrl = businessRegUrl;
      let bankUrl = businessBankUrl;

      if (isBusinessAccount) {
        if (businessRegFile) {
          const ext = getFileExtension(businessRegFile);
          const fileName = `reg_${user.email}_${Date.now()}${ext}`;
          const { data, error } = await supabase.storage
            .from("business")
            .upload(fileName, businessRegFile);
          if (!error) {
            const {
              data: { publicUrl },
            } = supabase.storage.from("business").getPublicUrl(data.path);
            regUrl = publicUrl;
          }
        }

        if (businessBankFile) {
          const ext = getFileExtension(businessBankFile);
          const fileName = `bank_${user.email}_${Date.now()}${ext}`;
          const { data, error } = await supabase.storage
            .from("business")
            .upload(fileName, businessBankFile);
          if (!error) {
            const {
              data: { publicUrl },
            } = supabase.storage.from("business").getPublicUrl(data.path);
            bankUrl = publicUrl;
          }
        }
      }

      // payout_buyer 테이블 업데이트
      const { error: payoutError } = await supabase
        .from("payout_buyer")
        .insert({
          user: user.email,
          bank_account_holder: userInfo?.name,
          bank_name: bankName,
          bank_account_number: bankAccountNumber,
          id_number1: isBusinessAccount ? null : idNumber1,
          id_number2: isBusinessAccount ? null : idNumber2,
          amount_melon: settlements.melon,
          amount_genie: settlements.genie,
          amount_vibe: settlements.vibe,
          amount_flo: settlements.flo,
          amount_global: settlements.global,
          amount_youtube: settlements.youtube,
          amount_total: settlements.remaining_amount,
          agreement: true,
          phone_number: userInfo?.phone_number,
          request_date: new Date().toISOString(),
          status: "[User] Request",
          settlement_month: selectedDate,
          national_tax: 0,
          local_tax: 0,
        });

      if (payoutError) throw payoutError;

      // 이메일 전송
      const subject = isBusinessAccount
        ? `[접수] 사업자 정산 신청 / ${user?.email}`
        : `[접수] 개인 정산 신청 / ${user?.email}`;
      const message = isBusinessAccount
        ? `<p>사업자 정산 신청이 접수되었습니다.</p>
        <p>신청자: ${userInfo?.name}</p>
        <p>신청 금액: ${Math.round(
          settlements.remaining_amount
        ).toLocaleString()}원</p>
        <p>정산월: ${selectedDate.slice(0, 4)}-${selectedDate.slice(4)}</p>
        `
        : `<p>개인 정산 신청이 접수되었습니다.</p>
        <p>신청자: ${userInfo?.name}</p>
        <p>주민등록번호: ${idNumber1}-${idNumber2}</p>
        <p>신청 금액: ${Math.round(
          settlements.remaining_amount
        ).toLocaleString()}원</p>
        <p>정산월: ${selectedDate.slice(0, 4)}-${selectedDate.slice(4)}</p>`;

      const attachments = [];
      if (isBusinessAccount) {
        if (regUrl) attachments.push({ path: regUrl, filename: regUrl });
        if (bankUrl) attachments.push({ path: bankUrl, filename: bankUrl });
      }

      await SendEmail({
        email: "contact@plpl.kr",
        subject,
        message,
        attachments,
        storageBucket: "business",
      });

      // user 테이블 업데이트
      await supabase
        .from("user")
        .update({
          bank_account_holder: userInfo?.name,
          bank_name: bankName,
          bank_account_number: bankAccountNumber,
          business_account: isBusinessAccount,
          business_reg_docu: regUrl,
          business_bank_account_file: bankUrl,
        })
        .eq("email", user.email);

      toast({
        title: "정산 신청이 완료되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      setIsRequestOpen(false);
      await fetchSettlements();
    } catch (error) {
      console.error("Error in payout request:", error);
      toast({
        title: "정산 신청 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    agreement,
    user.email,
    businessRegUrl,
    businessBankUrl,
    businessRegFile,
    businessBankFile,
    isBusinessAccount,
    bankName,
    bankAccountNumber,
    idNumber1,
    idNumber2,
    settlements,
    userInfo?.phone_number,
    selectedDate,
    getFileExtension,
    toast,
    fetchSettlements,
    userInfo?.name,
  ]);

  // 정산 신청 버튼 클릭 시 payout_buyer, user 정보 조회
  const handleOpenRequestModal = useCallback(async () => {
    if (!user?.email) return;

    try {
      const [payoutData, userData] = await Promise.all([
        supabase
          .from("payout_buyer")
          .select(
            "bank_account_holder, bank_name, bank_account_number, id_number1, id_number2, request_date"
          )
          .eq("user", user.email)
          .order("created_date", { ascending: false }),
        supabase
          .from("user")
          .select(
            "name, business_bank_account_file, business_account, business_reg_docu, phone_number"
          )
          .eq("email", user.email)
          .maybeSingle(),
      ]);

      const latestPayoutRow = payoutData.data?.[0];
      const userRow = userData.data;

      if (userRow) {
        setUserInfo({
          ...userRow,
          name: userRow.name || "",
          phone_number: userRow.phone_number || "",
        });
      }

      if (latestPayoutRow) {
        setBankAccountHolder(latestPayoutRow.bank_account_holder || "");
        setBankName(latestPayoutRow.bank_name || "");
        setBankAccountNumber(latestPayoutRow.bank_account_number || "");
        setIdNumber1(latestPayoutRow.id_number1 || "");
        setIdNumber2(latestPayoutRow.id_number2 || "");
        setHasPayoutRow(true);
      } else {
        setBankAccountHolder("");
        setBankName("");
        setBankAccountNumber("");
        setIdNumber1("");
        setIdNumber2("");
        setHasPayoutRow(false);
      }

      if (userRow) {
        setBusinessBankUrl(userRow.business_bank_account_file || "");
        setBusinessRegUrl(userRow.business_reg_docu || "");
        setIsBusinessAccount(
          typeof userRow.business_account === "string"
            ? userRow.business_account.toLowerCase() === "true"
            : !!userRow.business_account
        );
      } else {
        setBusinessBankUrl("");
        setBusinessRegUrl("");
        setIsBusinessAccount(false);
      }

      setIsRequestOpen(true);
    } catch (error) {
      console.error("Error opening request modal:", error);
      toast({
        title: "정보 조회 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  }, [user?.email, toast]);

  // 상태 텍스트 변환
  const getStatusText = useCallback((status) => {
    switch (status) {
      case "[User] Request":
        return "정산예정";
      case "[Admin] Completed":
        return "정산완료";
      default:
        return status;
    }
  }, []);

  // 정산신청 버튼 클릭 시
  const handleOpenSmsModal = async () => {
    await handlePayoutRequest();
  };

  // 플랫폼 상세 내역 불러오기
  const fetchPlatformDetail = async (platform, page = 1) => {
    setPlatformDetailLoading(true);
    try {
      // 테스트 모드에 따라 RPC 함수명 결정
      const rpcName = isTestMode
        ? "get_platform_breakdown_single_test"
        : "get_platform_breakdown_single";
      const { data } = await supabase.rpc(rpcName, {
        target_email: user.email,
        target_month: selectedDate,
        target_platform: platform,
        page_number: page,
      });
      setPlatformDetail(data || []);
      setPlatformDetailTotal(data && data.length > 0 ? data[0].total_count : 0);
    } finally {
      setPlatformDetailLoading(false);
    }
  };

  // 플랫폼 박스 클릭 핸들러
  const handlePlatformClick = (platform) => {
    if (selectedPlatform === platform) {
      setSelectedPlatform(null);
      setPlatformDetail([]);
      setPlatformDetailPage(1);
      setPlatformDetailTotal(0);
    } else {
      setSelectedPlatform(platform);
      setPlatformDetailPage(1);
      fetchPlatformDetail(platform, 1);
    }
  };

  // 페이지 변경 핸들러
  const handlePlatformDetailPageChange = (page) => {
    setPlatformDetailPage(page);
    setTempPage(page.toString());
    fetchPlatformDetail(selectedPlatform, page);
  };

  // 페이지 입력 핸들러
  const handlePageInput = (e) => {
    const value = e.target.value;
    setTempPage(value);
  };

  // 페이지 이동 버튼 핸들러
  const handlePageMove = () => {
    if (/^\d+$/.test(tempPage)) {
      const page = parseInt(tempPage);
      const maxPage = Math.ceil(platformDetailTotal / PAGE_SIZE);
      if (page >= 1 && page <= maxPage) {
        handlePlatformDetailPageChange(page);
      } else {
        setTempPage(platformDetailPage.toString());
      }
    } else {
      setTempPage(platformDetailPage.toString());
    }
  };

  // platformDetailPage가 바뀔 때마다 tempPage 동기화
  useEffect(() => {
    setTempPage(platformDetailPage.toString());
  }, [platformDetailPage]);

  // 정산월 변경/새로고침 시 페이지 초기화
  useEffect(() => {
    setPlatformDetailPage(1);
    setTempPage("1");
  }, [selectedDate]);

  // settings 테이블에서 settlement switch 값 불러오기
  const fetchSettlementSwitch = async () => {
    setSettlementSwitchLoading(true);
    try {
      const { data, error } = await supabase
        .from("settings")
        .select("setting_switch")
        .eq("setting_name", "settlement")
        .maybeSingle();
      if (!error && data) {
        setSettlementSwitch(!!data.setting_switch);
      } else {
        setSettlementSwitch(true); // 기본값 true
      }
    } finally {
      setSettlementSwitchLoading(false);
    }
  };

  // localStorage에서 테스트 모드 상태 감지
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem("isTestMode");
      setIsTestMode(saved === "true");
    };

    // 초기 로드 시 테스트 모드 상태 확인
    const saved = localStorage.getItem("isTestMode");
    setIsTestMode(saved === "true");

    // storage 이벤트 리스너 추가 (다른 탭에서 변경 시)
    window.addEventListener("storage", handleStorageChange);

    // 같은 탭에서의 변경도 감지하기 위해 주기적으로 확인
    const interval = setInterval(() => {
      const current = localStorage.getItem("isTestMode");
      if (current !== (isTestMode ? "true" : "false")) {
        setIsTestMode(current === "true");
      }
    }, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [isTestMode]);

  // 테스트 모드 변경 시 데이터 다시 로드
  useEffect(() => {
    if (!user?.email) return;
    fetchSettlements();
  }, [isTestMode, user?.email, fetchSettlements]);

  // useEffect로 데이터 로딩
  useEffect(() => {
    if (!user?.email) return;
    fetchSettlements();
    fetchSettlementSwitch();
  }, [user?.email, selectedDate, fetchSettlements]);

  // 정산월 변경 시 상세 내역 선택 초기화
  useEffect(() => {
    setSelectedPlatform(null);
    setPlatformDetail([]);
    setPlatformDetailPage(1);
    setPlatformDetailTotal(0);
  }, [selectedDate]);

  useEffect(() => {
    // payoutHistory, idNumber1, idNumber2가 모두 준비된 후 최초 1회만 판단
    if (payoutHistory.length > 0 && idNumber1 && idNumber2) {
      setShowIdInput(false);
    } else {
      setShowIdInput(true);
    }
    // eslint-disable-next-line
  }, [payoutHistory.length]);

  return (
    <Container maxW="container.xl" py={8}>
      <Flex justify="flex-end" mb={6}>
        <Select
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          width="200px"
        >
          {getDateOptions().map((date) => (
            <option key={date} value={date}>
              {date.slice(0, 4)}
              {date.slice(4)}
            </option>
          ))}
        </Select>
      </Flex>

      {loading ? (
        <Center py={10}>
          <Spinner size="xl" />
        </Center>
      ) : (
        <VStack spacing={8}>
          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={6}
            width="100%"
          >
            {[
              ["melon", "멜론"],
              ["genie", "지니"],
              ["vibe", "바이브"],
              ["flo", "플로"],
              ["youtube", "유튜브"],
              ["global", "기타 해외"],
            ].map(([platform, label]) => (
              <Box
                key={platform}
                bg="white"
                p={6}
                borderRadius="xl"
                boxShadow="md"
                textAlign="center"
                cursor="pointer"
                border={
                  selectedPlatform === platform ? "2px solid #3182ce" : "none"
                }
                onClick={() => handlePlatformClick(platform)}
                position="relative"
              >
                <Image
                  src={platformLogos[platform]}
                  alt={`${platform} logo`}
                  boxSize="100px"
                  mx="auto"
                  mb={4}
                />
                <Text fontSize="2xl" fontWeight="bold">
                  {Math.round(settlements[platform] || 0).toLocaleString()}원
                </Text>
                {settlements[platform] <
                  settlements[`${platform}_original`] && (
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    (정산 신청(완료) 금액{" "}
                    {Math.round(
                      settlements[`${platform}_original`] -
                        settlements[platform]
                    ).toLocaleString()}
                    원)
                  </Text>
                )}
              </Box>
            ))}
          </SimpleGrid>

          {/* 플랫폼별 상세 내역 테이블 (최종정산금 카드 위에 위치) */}
          {selectedPlatform && (
            <Box w="100%" mt={8} mb={2}>
              <Text
                fontWeight="bold"
                fontSize={{ base: "md", md: "lg" }}
                mb={2}
              >
                {
                  [
                    ["melon", "멜론"],
                    ["genie", "지니"],
                    ["vibe", "바이브"],
                    ["flo", "플로"],
                    ["youtube", "유튜브"],
                    ["global", "기타 해외"],
                  ].find(([key]) => key === selectedPlatform)?.[1]
                }{" "}
                정산 상세 내역
              </Text>
              <Box
                bg="white"
                borderRadius="xl"
                boxShadow="md"
                p={{ base: 2, md: 4 }}
              >
                {platformDetailLoading ? (
                  <Center py={10}>
                    <Spinner />
                  </Center>
                ) : platformDetail.length === 0 ? (
                  <Text
                    textAlign="center"
                    color="gray.500"
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    데이터가 없습니다.
                  </Text>
                ) : (
                  <>
                    <Table variant="simple" size={{ base: "sm", md: "md" }}>
                      <Thead bg="gray.50">
                        <Tr>
                          <Th fontSize={{ base: "xs", md: "sm" }}>음반</Th>
                          <Th
                            textAlign="center"
                            fontSize={{ base: "xs", md: "sm" }}
                          >
                            스트리밍/다운로드
                          </Th>
                          <Th
                            textAlign="center"
                            fontSize={{ base: "xs", md: "sm" }}
                          >
                            정산금
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {platformDetail.map((item, idx) => (
                          <Tr
                            key={item.track_code + idx}
                            _hover={{ bg: "blue.50" }}
                            style={{ transition: "background 0.2s" }}
                          >
                            <Td>
                              <Flex align="center" gap={{ base: 2, md: 3 }}>
                                <Image
                                  src={item.cover_url || item.cover}
                                  alt={item.name_track}
                                  boxSize={{ base: "36px", md: "48px" }}
                                  borderRadius="md"
                                  objectFit="cover"
                                  bg="white"
                                  fallbackSrc="/images/default-album.png"
                                />
                                <Box>
                                  <Text
                                    fontWeight="medium"
                                    fontSize={{ base: "xs", md: "sm" }}
                                  >
                                    {item.name_track}
                                  </Text>
                                  <Text
                                    fontSize={{ base: "xs", md: "sm" }}
                                    color="gray.500"
                                  >
                                    {item.name_musician}
                                  </Text>
                                  <Text
                                    fontSize={{ base: "2xs", md: "xs" }}
                                    color="gray.400"
                                  >
                                    {item.name_album}
                                  </Text>
                                </Box>
                              </Flex>
                            </Td>
                            <Td
                              textAlign="center"
                              fontSize={{ base: "xs", md: "sm" }}
                            >
                              {Number(
                                item.stream_download_total || 0
                              ).toLocaleString()}
                            </Td>
                            <Td
                              textAlign="center"
                              fontSize={{ base: "xs", md: "sm" }}
                            >
                              {Math.round(
                                item.platform_amount || 0
                              ).toLocaleString()}
                              원
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                    {/* 페이지네이션 */}
                    {platformDetailTotal > PAGE_SIZE && (
                      <Flex
                        justify="center"
                        mt={{ base: 2, md: 4 }}
                        align="center"
                        flexWrap="wrap"
                        gap={{ base: 1, md: 2 }}
                      >
                        <Button
                          size={{ base: "xs", md: "sm" }}
                          onClick={() => handlePlatformDetailPageChange(1)}
                          isDisabled={platformDetailPage === 1}
                          colorScheme="gray"
                          variant="outline"
                        >
                          처음
                        </Button>
                        <Button
                          size={{ base: "xs", md: "sm" }}
                          onClick={() =>
                            handlePlatformDetailPageChange(
                              platformDetailPage - 1
                            )
                          }
                          isDisabled={platformDetailPage === 1}
                          colorScheme="gray"
                          variant="outline"
                        >
                          이전
                        </Button>
                        <HStack spacing={1} mx={{ base: 1, md: 2 }}>
                          <Input
                            value={tempPage}
                            onChange={handlePageInput}
                            size={{ base: "xs", md: "sm" }}
                            width={{ base: "40px", md: "50px" }}
                            textAlign="center"
                            fontSize={{ base: "xs", md: "sm" }}
                          />
                          <Button
                            size={{ base: "xs", md: "sm" }}
                            onClick={handlePageMove}
                            ml={1}
                            colorScheme="gray"
                            variant="outline"
                            minW="40px"
                            px={2}
                            color="gray.600"
                            borderColor="gray.300"
                            _hover={{
                              bg: "gray.100",
                              borderColor: "gray.400",
                            }}
                          >
                            이동
                          </Button>
                          <Text fontSize={{ base: "xs", md: "sm" }}>
                            / {Math.ceil(platformDetailTotal / PAGE_SIZE)}
                          </Text>
                        </HStack>
                        <Button
                          size={{ base: "xs", md: "sm" }}
                          onClick={() =>
                            handlePlatformDetailPageChange(
                              platformDetailPage + 1
                            )
                          }
                          isDisabled={
                            platformDetailPage >=
                            Math.ceil(platformDetailTotal / PAGE_SIZE)
                          }
                          colorScheme="gray"
                          variant="outline"
                        >
                          다음
                        </Button>
                        <Button
                          size={{ base: "xs", md: "sm" }}
                          onClick={() =>
                            handlePlatformDetailPageChange(
                              Math.ceil(platformDetailTotal / PAGE_SIZE)
                            )
                          }
                          isDisabled={
                            platformDetailPage >=
                            Math.ceil(platformDetailTotal / PAGE_SIZE)
                          }
                          colorScheme="gray"
                          variant="outline"
                        >
                          마지막
                        </Button>
                      </Flex>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}

          <Divider />

          <Box
            bg="blue.50"
            p={6}
            borderRadius="xl"
            boxShadow="md"
            textAlign="center"
            width="100%"
          >
            <Text fontSize="xl" mb={2}>
              정산 가능 금액
            </Text>
            <Text fontSize="3xl" fontWeight="bold" color="blue.600">
              {Number(selectedDate) <= 202412
                ? "-"
                : `${Math.round(
                    settlements.remaining_amount || 0
                  ).toLocaleString()}원`}
            </Text>
            {settlements.remaining_amount < settlements.total_sum && (
              <Text fontSize="sm" color="gray.600" mt={2}>
                (이미 신청된 금액{" "}
                {Math.round(
                  settlements.total_sum - settlements.remaining_amount
                ).toLocaleString()}
                원 차감)
              </Text>
            )}
          </Box>

          <HStack spacing={8} justify="center" width="100%">
            <Button
              colorScheme="blue"
              size="lg"
              height="80px"
              width="280px"
              fontSize="24px"
              onClick={settlementSwitch ? handleOpenRequestModal : undefined}
              isDisabled={
                settlements.remaining_amount <= 0 ||
                !settlementSwitch ||
                settlementSwitchLoading ||
                Number(selectedDate) <= 202412
              }
            >
              {settlementSwitchLoading
                ? "로딩 중..."
                : !settlementSwitch
                ? "정산 일시 중단"
                : settlements.remaining_amount < 1
                ? "정산 신청 불가"
                : Number(selectedDate) <= 202412
                ? "정산 신청 불가"
                : "정산 신청"}
            </Button>
            <Button
              colorScheme="gray"
              size="lg"
              height="80px"
              width="280px"
              fontSize="24px"
              onClick={() => {
                setIsHistoryOpen(true);
                fetchPayoutHistory();
              }}
            >
              정산 내역
            </Button>
          </HStack>
          {!settlementSwitch && !settlementSwitchLoading && (
            <Text color="red.500" fontWeight="bold" fontSize="md">
              현재 정산 작업으로 인해 일시 중단되었습니다. 조금만 기다려주세요.
            </Text>
          )}
          {/* 2024년 12월 이전 정산 안내 */}
          {Number(selectedDate) <= 202412 && (
            <Text color="red.500" fontWeight="bold" fontSize="md" mt={2}>
              2024년 12월 이전 정산은 contact@plpl.kr로 문의 부탁드립니다.
            </Text>
          )}
        </VStack>
      )}

      {/* 정산 신청 모달 */}
      <Modal
        isOpen={isRequestOpen}
        onClose={() => {
          setIsRequestOpen(false);
          setEditIdNumber(false);
          setIdNumber1("");
          setIdNumber2("");
          fetchPayoutHistory();
          fetchSettlements();
        }}
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent maxH="160vh">
          <ModalHeader pb={0}>
            <VStack align="stretch" spacing={2}>
              <Flex justify="space-between" align="center" position="relative">
                <Text fontSize="xl">정산 신청</Text>
                <ModalCloseButton position="absolute" right="-8px" top="-8px" />
              </Flex>
            </VStack>
          </ModalHeader>
          <ModalBody pb={6} maxH="160vh">
            <VStack spacing={4}>
              {/* 정산월/최종정산금 */}
              <Flex
                justify="space-between"
                align="center"
                bg="gray.50"
                p={4}
                borderRadius="md"
                width="100%"
              >
                <VStack align="flex-start" spacing={1}>
                  <Text color="gray.600">정산월</Text>
                  <Text fontSize="xl" fontWeight="bold">
                    {selectedDate.slice(0, 4)}-{selectedDate.slice(4)}
                  </Text>
                </VStack>
                <VStack align="flex-end" spacing={1}>
                  <Text color="gray.600">정산 가능 금액</Text>
                  <Text fontSize="xl" fontWeight="bold" color="blue.600">
                    {Math.round(
                      settlements.remaining_amount || 0
                    ).toLocaleString()}
                    원
                  </Text>
                  {settlements.remaining_amount < settlements.total_sum && (
                    <Text fontSize="sm" color="gray.600">
                      (정산 신청(완료) 금액{" "}
                      {Math.round(
                        settlements.total_sum - settlements.remaining_amount
                      ).toLocaleString()}
                      원)
                    </Text>
                  )}
                </VStack>
              </Flex>
              {/* 입력 폼 시작 */}
              <FormControl isRequired>
                <FormLabel>신청인</FormLabel>
                <Input value={userInfo?.name || ""} isDisabled={true} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>은행</FormLabel>
                <Select
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="은행 선택"
                >
                  <option value="NH농협">NH농협</option>
                  <option value="카카오뱅크">카카오뱅크</option>
                  <option value="KB국민">KB국민</option>
                  <option value="신한">신한</option>
                  <option value="우리">우리</option>
                  <option value="토스뱅크">토스뱅크</option>
                  <option value="IBK기업">IBK기업</option>
                  <option value="KEB하나">KEB하나</option>
                  <option value="새마을">새마을</option>
                  <option value="부산">부산</option>
                  <option value="대구">대구</option>
                  <option value="케이뱅크">케이뱅크</option>
                  <option value="신협">신협</option>
                  <option value="우체국">우체국</option>
                  <option value="SC제일">SC제일</option>
                  <option value="경남">경남</option>
                  <option value="광주">광주</option>
                  <option value="수협">수협</option>
                  <option value="전북">전북</option>
                  <option value="저축">저축</option>
                  <option value="제주">제주</option>
                  <option value="씨티">씨티</option>
                  <option value="KDB산업">KDB산업</option>
                  <option value="산림조합">산림조합</option>
                  <option value="SBI저축">SBI저축</option>
                  <option value="BOA">BOA</option>
                  <option value="중국">중국</option>
                  <option value="HSBC">HSBC</option>
                  <option value="중국공상">중국공상</option>
                  <option value="도이치">도이치</option>
                  <option value="JP모건">JP모건</option>
                  <option value="BNP 파리바">BNP 파리바</option>
                  <option value="중국건설">중국건설</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>계좌번호</FormLabel>
                <Input
                  value={bankAccountNumber}
                  onChange={(e) => setBankAccountNumber(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={isBusinessAccount}
                  onChange={(e) => {
                    setIsBusinessAccount(e.target.checked);
                    setEditBusinessFile(false);
                    setEditIdNumber(false);
                  }}
                >
                  법인/개인 사업자 회원일 경우, 클릭
                </Checkbox>
              </FormControl>
              {/* 주민등록번호 입력/마스킹/수정 */}
              <>
                {isBusinessAccount ? (
                  businessRegUrl && businessBankUrl && !editBusinessFile ? (
                    <VStack w="100%" align="start">
                      <Button
                        as="a"
                        href={businessRegUrl}
                        target="_blank"
                        colorScheme="blue"
                        variant="outline"
                        leftIcon={<DownloadIcon />}
                      >
                        사업자등록증 다운로드
                      </Button>
                      <Button
                        as="a"
                        href={businessBankUrl}
                        target="_blank"
                        colorScheme="blue"
                        variant="outline"
                        leftIcon={<DownloadIcon />}
                      >
                        사업자 통장 다운로드
                      </Button>
                      <Button
                        onClick={() => setEditBusinessFile(true)}
                        colorScheme="gray"
                        size="sm"
                      >
                        사업자 파일 수정하기
                      </Button>
                    </VStack>
                  ) : (
                    <VStack w="100%" align="start">
                      <FormControl isRequired={editBusinessFile}>
                        <FormLabel>사업자등록증 파일</FormLabel>
                        <Input
                          type="file"
                          accept="application/pdf,image/*"
                          onChange={(e) =>
                            setBusinessRegFile(e.target.files[0])
                          }
                        />
                      </FormControl>
                      <FormControl isRequired={editBusinessFile}>
                        <FormLabel>사업자 통장 파일</FormLabel>
                        <Input
                          type="file"
                          accept="application/pdf,image/*"
                          onChange={(e) =>
                            setBusinessBankFile(e.target.files[0])
                          }
                        />
                      </FormControl>
                      <HStack>
                        <Button
                          onClick={() => setEditBusinessFile(false)}
                          colorScheme="gray"
                          size="sm"
                        >
                          취소
                        </Button>
                        <Button
                          onClick={() => setEditBusinessFile(false)}
                          colorScheme="blue"
                          size="sm"
                        >
                          완료
                        </Button>
                      </HStack>
                    </VStack>
                  )
                ) : showIdInput ? (
                  <VStack w="100%" align="start">
                    <HStack w="100%">
                      <FormControl isRequired>
                        <FormLabel>주민등록번호 앞 6자리</FormLabel>
                        <Input
                          maxLength={6}
                          value={idNumber1}
                          onChange={(e) =>
                            setIdNumber1(e.target.value.replace(/[^0-9]/g, ""))
                          }
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel>주민등록번호 뒷 7자리</FormLabel>
                        <Input
                          maxLength={7}
                          type="password"
                          value={idNumber2}
                          onChange={(e) =>
                            setIdNumber2(e.target.value.replace(/[^0-9]/g, ""))
                          }
                        />
                      </FormControl>
                    </HStack>
                  </VStack>
                ) : null}
                <FormControl isRequired>
                  <Checkbox
                    isChecked={agreement}
                    onChange={(e) => setAgreement(e.target.checked)}
                  >
                    고유 식별 정보 수집 및 원천 징수 신고 안내를 확인하고 이에
                    동의합니다.
                  </Checkbox>
                </FormControl>
              </>
              {/* 안내문 아코디언 */}
              <Box mt={12} width="100%" maxHeight="500px" overflowY="auto">
                <Accordion allowMultiple defaultIndex={[]}>
                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="bold">
                        원천 징수 및 소득 신고 안내
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4} fontSize="sm">
                      플플 및 레이블리 앱에서 음반을 구매한 후 발생하는 정산금은
                      국세/지방세 과세대상입니다.
                      <br />
                      정산금이 발생하여 정산 요청을 하시면 사업소득에 대한
                      국세(3%)와 지방세(국세의 10%)가 부과되어, 총 약 3.3%의
                      세금이 원천 징수되며 레이블리 운영사인 (주)루나르트가
                      국세청에 신고합니다.
                      <br />
                      <br />
                      또 해당 소득은 기타소득으로 분류되며 해당 소득은
                      종합소득세 신고대상임을 알려드립니다.
                      <br />
                      연간 총 기타소득이 3백만원을 초과하는 경우에는 전체금액에
                      대해 종합소득과세표준에 합산하여 매년 5월말까지 종합소득세
                      신고해야 합니다.
                      <br />
                      <br />
                      <b>
                        * 아래의 경우 종합소득세 신고 대상이므로, 매년 5월
                        종합소득세 신고기간 때 반드시 신고하여 불이익이 발생하지
                        않도록 유의부탁드립니다.
                      </b>
                      <br />
                      1. 직전년도 1월~12월 귀속 기타소득 합계액이 연간 300만원
                      초과
                      <br />
                      2. 2개 이상의 회사에서 기타소득 합계액이 연간 300만원 초과
                      <br />
                      3. (주)루나르트로부터 기타소득(정산금) 합계액이 연간
                      300만원 초과
                      <br />
                      4. 2개 이상의 회사 및 (주)루나르트(정산금 제외)금액의
                      합산액이 연간 300만원 초과
                      <br />
                      <br />
                      종합소득세 신고방법
                      <br />
                      · 신고기간 : 매년 5월1일~5월 31일
                      <br />
                      · 신고방법:
                      <br />
                      1. 본인이 직접 국세청 사이트(http://www.hometax.go.kr)에서
                      신고
                      <br />
                      2. 소재 관할 세무서 방문 신고
                      <br />
                      3. 세무사 사무실에 의뢰
                      <br />
                      <br />※ 종합소득세 미신고, 원천 징수 거부 등에 따른
                      불이익으로 인한 피해는 (주)루나르트에서 보상해드리지 못
                      하오니 참고부탁드립니다.
                    </AccordionPanel>
                  </AccordionItem>
                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="bold">
                        고유 식별 정보 수집에 대한 안내
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4} fontSize="sm">
                      (고유식별정보의 수집 및 이용 목적)
                      <br />
                      (주)루나르트는 아래의 목적을 위해 고객의 고유식별정보를
                      수집, 보관 및 처리할 수 있습니다.
                      <br />
                      <br />
                      (1) 소득세법 제 145조에 따라 기타소득 등에 대한
                      지급명세서를 관할 세무서 및 세무/회계 대행
                      법인(동현회계법인)에 소득 신고를 위해 관련 정보 제출시,
                      기타소득자의 주민등록번호 기재를 위함
                      <br />
                      <br />
                      [수집하는 개인정보의 항목]
                      <br />
                      ① 주민등록번호(내국인)
                      <br />
                      ② 외국인등록번호(비거주자/재외국인)
                      <br />
                      <br />
                      [개인정보의 보유 및 이용기간]
                      <br />
                      "회사"는 소득 정산을 위하여 필요한 기간 동안 혹은
                      탈퇴시까지 이용자의 고유식별정보를 보유합니다.
                      <br />※ 정산 신청을 위해 상기 내용과 같이 고유식별정보의
                      처리에 동의합니다. 고유식별정보 처리 동의는 거부할 수
                      있으나, 거부 시에는 정산 서비스를 이용 및 제공 받으실 수
                      없습니다.
                    </AccordionPanel>
                  </AccordionItem>
                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="bold">
                        정산 안내
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4} fontSize="sm">
                      해당 정산금은 플플 및 레이블리에서 구매한 음반에서 발생한
                      저작인접권입니다.
                      <br />
                      정산 신청 후에는 복구가 불가능하오니 유의하시어
                      신청부탁드리며, 정산금에 따라 사업소득에 대한
                      국세/지방세(약 3.3%), 종합소득세 등의 세금이 발생할 수
                      있습니다.
                      <br />
                      세금에 관한 자세한 내용은 아래 '원천 징수 및 신고 안내'를
                      참고부탁드립니다.
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </Box>
              <Button
                colorScheme="blue"
                width="100%"
                onClick={() => {
                  if (
                    !bankName ||
                    !bankAccountNumber ||
                    !agreement ||
                    (isBusinessAccount &&
                      editBusinessFile &&
                      (!businessRegFile || !businessBankFile)) ||
                    (!isBusinessAccount && (!idNumber1 || !idNumber2))
                  ) {
                    return;
                  }
                  if (!isBusinessAccount && payoutHistory.length === 0) {
                    if (!/^\d{6}$/.test(idNumber1)) {
                      toast({
                        title: "주민등록번호 앞자리는 6자리 숫자여야 합니다.",
                        status: "error",
                        duration: 2000,
                        isClosable: true,
                      });
                      return;
                    }
                    if (!/^\d{7}$/.test(idNumber2)) {
                      toast({
                        title: "주민등록번호 뒷자리는 7자리 숫자여야 합니다.",
                        status: "error",
                        duration: 2000,
                        isClosable: true,
                      });
                      return;
                    }
                  }
                  if (
                    isBusinessAccount &&
                    !(
                      (businessRegUrl && businessBankUrl) ||
                      (businessRegFile && businessBankFile)
                    )
                  ) {
                    toast({
                      title: "사업자 등록증과 통장 파일이 필요합니다.",
                      status: "error",
                      duration: 2000,
                      isClosable: true,
                    });
                    return;
                  }
                  setEditIdNumber(false);
                  handleOpenSmsModal();
                }}
                isDisabled={
                  !bankName ||
                  !bankAccountNumber ||
                  !agreement ||
                  (isBusinessAccount &&
                    editBusinessFile &&
                    (!businessRegFile || !businessBankFile)) ||
                  (!isBusinessAccount && (!idNumber1 || !idNumber2)) ||
                  (isBusinessAccount &&
                    !(
                      (businessRegUrl && businessBankUrl) ||
                      (businessRegFile && businessBankFile)
                    ))
                }
                isLoading={submitting}
              >
                {!bankName ||
                !bankAccountNumber ||
                !agreement ||
                (isBusinessAccount &&
                  editBusinessFile &&
                  (!businessRegFile || !businessBankFile)) ||
                (!isBusinessAccount && (!idNumber1 || !idNumber2)) ||
                (isBusinessAccount &&
                  !(
                    (businessRegUrl && businessBankUrl) ||
                    (businessRegFile && businessBankFile)
                  ))
                  ? "필수 사항 입력 및 동의 후 신청할 수 있어요"
                  : "정산 신청"}
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 정산 내역 모달 */}
      <Modal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        size={{ base: "full", md: "4xl" }}
      >
        <ModalOverlay />
        <ModalContent maxH={{ base: "100vh", md: "90vh" }}>
          <ModalHeader>
            <Flex align="center" gap={2}>
              <Box
                bg="blue.100"
                borderRadius="full"
                p={2}
                display="flex"
                alignItems="center"
              >
                <DownloadIcon color="blue.500" boxSize={5} />
              </Box>
              <Text
                fontWeight="bold"
                fontSize={{ base: "lg", md: "xl" }}
                color="blue.700"
              >
                정산 내역
              </Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {loadingHistory ? (
              <Center py={10}>
                <Spinner />
              </Center>
            ) : (
              <Box
                bg="gray.50"
                borderRadius="xl"
                boxShadow="md"
                p={{ base: 3, md: 6 }}
              >
                <Table variant="simple" size={{ base: "sm", md: "md" }}>
                  <Thead bg="gray.100">
                    <Tr>
                      <Th
                        textAlign="center"
                        fontSize={{ base: "xs", md: "sm" }}
                      >
                        정산월
                      </Th>
                      <Th
                        textAlign="center"
                        fontSize={{ base: "xs", md: "sm" }}
                      >
                        정산 금액
                      </Th>
                      <Th
                        textAlign="center"
                        fontSize={{ base: "xs", md: "sm" }}
                      >
                        상태
                      </Th>
                      <Th
                        textAlign="center"
                        fontSize={{ base: "xs", md: "sm" }}
                      >
                        신청일
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {payoutHistory.map((history, idx) => (
                      <Tr
                        key={history.settlement_month}
                        _hover={{ bg: "blue.50" }}
                        style={{ transition: "background 0.2s" }}
                      >
                        <Td
                          textAlign="center"
                          fontWeight="medium"
                          fontSize={{ base: "xs", md: "sm" }}
                        >
                          {history.settlement_month.slice(0, 4)}-
                          {history.settlement_month.slice(4)}
                        </Td>
                        <Td
                          textAlign="center"
                          fontWeight="bold"
                          color="blue.700"
                          fontSize={{ base: "xs", md: "sm" }}
                        >
                          {Math.round(
                            history.amount_total || 0
                          ).toLocaleString()}
                          원
                        </Td>
                        <Td
                          textAlign="center"
                          fontSize={{ base: "xs", md: "sm" }}
                        >
                          <Box
                            display="inline-block"
                            px={{ base: 2, md: 3 }}
                            py={{ base: 0.5, md: 1 }}
                            borderRadius="full"
                            bg={
                              history.status === "[Admin] Completed"
                                ? "green.100"
                                : "yellow.100"
                            }
                            color={
                              history.status === "[Admin] Completed"
                                ? "green.700"
                                : "yellow.700"
                            }
                            fontWeight="bold"
                          >
                            {getStatusText(history.status)}
                          </Box>
                        </Td>
                        <Td
                          textAlign="center"
                          fontSize={{ base: "xs", md: "sm" }}
                        >
                          {history.created_date
                            ? new Date(history.created_date)
                                .toISOString()
                                .slice(0, 10)
                            : "-"}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {payoutHistory.length === 0 && (
                  <Text
                    textAlign="center"
                    color="gray.400"
                    py={{ base: 4, md: 8 }}
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    정산 내역이 없습니다.
                  </Text>
                )}
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default Report;
